import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { getAttributes, getLoggedUserData, getOrganization, getUserSettings } from '@/helpers/api'
import { isNil } from '@/helpers/helpers'
import { updateIntercomUserDataSettings } from '@/helpers/intercom'
import { Attributes, User } from '@/interfaces/users'
import { getUrl } from '@/helpers/uri'
import { useWindowFocusCallback } from '@/hooks/useWindowFocusCallback'

interface AuthContextProps {
  isLoggedIn: boolean
  isPrivateProjectPage: boolean
  setIsPrivateProjectPage: (isPrivateProjectPage: boolean) => void
  isErrorPage: boolean
  setIsErrorPage: (isErrorPage: boolean) => void
  userData: User | null
  checkedUserData: boolean
  attributes: Attributes | null
  organizationName: string | null
  isBannerDisplayed: boolean
  setIsBannerDisplayed: (isBannerDisplayed: boolean) => void
}

const AuthContext = createContext<AuthContextProps>({
  isLoggedIn: false,
  isPrivateProjectPage: false,
  setIsPrivateProjectPage: () => {},
  isErrorPage: false,
  setIsErrorPage: () => {},
  userData: null,
  checkedUserData: false,
  attributes: null,
  organizationName: null,
  isBannerDisplayed: false,
  setIsBannerDisplayed: () => {}
})

export const AuthContextProvider = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  const router = useRouter()
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false)
  const [isPrivateProjectPage, setIsPrivateProjectPage] = useState<boolean>(false)
  const [isErrorPage, setIsErrorPage] = useState<boolean>(false)
  const [userData, setUserData] = useState<User | null>(null)
  const [checkedUserData, setCheckedUserData] = useState<boolean>(false)
  const [attributes, setAttributes] = useState<Attributes | null>(null)
  const [organizationName, setOrganizationName] = useState<string | null>(null)
  const [isBannerDisplayed, setIsBannerDisplayed] = useState<boolean>(false)
  const BANNER_DELAY = 2000

  const updateUserState = useCallback(
    async (redirectOnError?: boolean): Promise<void> => {
      const wasLoggedIn = isLoggedIn
      const fetchedUserData = await getLoggedUserData()
      if (!isNil(fetchedUserData)) {
        const fetchedAttributes = await getAttributes(fetchedUserData.uidExt)
        if (!isNil(fetchedAttributes)) {
          setAttributes(fetchedAttributes)
        }

        if (!isNil(fetchedUserData.organizationInfo?.organizationId)) {
          const organization = await getOrganization(fetchedUserData.organizationInfo.organizationId)
          setOrganizationName(organization?.name)
        }

        getUserSettings(fetchedUserData.username).then((settings) => {
          if (
            !isNil(settings) &&
            settings?.onboarding_finished !== 'true' &&
            !isNil(settings?.social_signin_provider)
          ) {
            window.location.href = getUrl('/onboarding/')
          }
        })

        setIsLoggedIn(fetchedUserData?.username != null)
        setUserData(fetchedUserData)
        updateIntercomUserDataSettings(fetchedUserData)

        /**
         * If user was not logged in and is now logged in, reload page to get new data,
         * because if it's an error page, it means that it could be private project page
         * that the user could have access to now.
         **/
        if (!wasLoggedIn && redirectOnError && isErrorPage) {
          window.location.reload()
        }
      } else {
        // If the page is private project and the user is not logged in, redirect to sign in page
        if (isPrivateProjectPage) {
          window.location.href = getUrl('/signin/')
        } else {
          setIsLoggedIn(false)
          setUserData(null)
        }
      }
      setCheckedUserData(true)
    },
    [isPrivateProjectPage, isErrorPage, isLoggedIn]
  )

  useWindowFocusCallback(() => {
    updateUserState(true)
  })

  useEffect(() => {
    updateUserState()
  }, [])

  useEffect(() => {
    setIsPrivateProjectPage(false)
    setIsErrorPage(false)
  }, [router.pathname])

  useEffect(() => {
    // Using delay to prevent the ResizeObserver loop to be triggered before the banner transform animation finishes
    const timer = setTimeout(() => {
      setIsBannerDisplayed(!isLoggedIn && checkedUserData)
    }, BANNER_DELAY)

    return () => clearTimeout(timer)
  }, [BANNER_DELAY, isLoggedIn, checkedUserData, setIsBannerDisplayed])

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        userData,
        isPrivateProjectPage,
        setIsPrivateProjectPage,
        isErrorPage,
        setIsErrorPage,
        checkedUserData,
        attributes,
        organizationName,
        isBannerDisplayed,
        setIsBannerDisplayed
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuthContext = (): AuthContextProps => {
  return useContext(AuthContext)
}
